import { BellOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Badge, Button, Dropdown, Empty, List, Space, Typography } from 'antd';
import { notificationsApi } from '../notifications.api';
import { enablePush, isPushSupported } from '../push';

export function NotificationBell() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const key = ['notifications'] as const;

  const query = useQuery({
    queryKey: key,
    queryFn: notificationsApi.list,
    refetchInterval: 20_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  const markAll = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: invalidate,
  });

  const enable = useMutation({
    mutationFn: enablePush,
    onSuccess: () => message.success('Notifications enabled'),
    onError: (e) =>
      message.error(e instanceof Error ? e.message : 'Could not enable notifications'),
  });

  const items = query.data?.items ?? [];
  const unread = query.data?.unread ?? 0;

  const panel = (
    <div
      style={{
        width: 340,
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 12,
        }}
      >
        <Typography.Text strong>Notifications</Typography.Text>
        <Space>
          {isPushSupported() ? (
            <Button size="small" onClick={() => enable.mutate()} loading={enable.isPending}>
              Enable push
            </Button>
          ) : null}
          <Button size="small" type="link" onClick={() => markAll.mutate()}>
            Mark all read
          </Button>
        </Space>
      </div>
      <div style={{ maxHeight: 360, overflowY: 'auto', padding: '0 12px 12px' }}>
        {items.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications" />
        ) : (
          <List
            size="small"
            dataSource={items}
            renderItem={(n) => (
              <List.Item>
                <List.Item.Meta
                  title={
                    <span style={{ fontWeight: n.read ? 400 : 600 }}>
                      {n.title} {n.read ? null : <Badge status="processing" />}
                    </span>
                  }
                  description={n.body}
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );

  return (
    <Dropdown trigger={['click']} dropdownRender={() => panel} placement="bottomRight">
      <Badge count={unread} size="small">
        <Button type="text" icon={<BellOutlined style={{ color: '#fff', fontSize: 18 }} />} />
      </Badge>
    </Dropdown>
  );
}
